import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './Comments.css';

function Comments({ entityType, entityId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchComments();
  }, [entityType, entityId]);

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/comments?targetType=${entityType}&targetId=${entityId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      setComments(data.comments || []);
      setError('');
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          targetType: entityType,
          targetId: entityId,
          comment: newComment
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to add comment');
        return;
      }

      setComments([data.comment, ...comments]);
      setNewComment('');
      setError('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Error adding comment: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/.netlify/functions/api/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        setError('Failed to delete comment');
        return;
      }

      setComments(comments.filter(c => c._id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Error deleting comment');
    }
  };

  return (
    <div className="comments-section">
      <h3>Comments & Notes</h3>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleAddComment} className="comment-form">
        <div className="form-group">
          <label>Add Note (Admin Only)</label>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a note about this item..."
            rows="3"
            disabled={loading}
          />
        </div>
        <button type="submit" disabled={loading} className="btn-submit">
          {loading ? 'Adding...' : 'Add Note'}
        </button>
      </form>

      <div className="comments-list">
        {comments.length === 0 ? (
          <p className="no-comments">No notes yet</p>
        ) : (
          comments.map(comment => (
            <div key={comment._id} className="comment-item">
              <div className="comment-header">
                <div className="comment-meta">
                  <span className="comment-author">
                    {comment.user?.name || 'Unknown User'}
                  </span>
                  <span className="comment-email">
                    ({comment.user?.email || 'unknown@email.com'})
                  </span>
                  <span className="comment-date">
                    {new Date(comment.createdAt).toLocaleString()}
                  </span>
                </div>
                {user?.email === comment.user?.email && (
                  <button
                    className="btn-delete-comment"
                    onClick={() => handleDeleteComment(comment._id)}
                    title="Delete comment"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="comment-text">{comment.comment}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Comments;
